import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Card,
  CardContent,
  CardActions,
  Typography,
  Avatar,
  CircularProgress,
  Grid,
  IconButton,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import PhoneIcon from "@mui/icons-material/Phone";
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useAxios from "../../hooks/useAxios"; // adjust path as needed
import "./ProfilePage.css";
import Header from "../../components/Header/Header";
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import LoopIcon from '@mui/icons-material/Loop';
import { useNavigate } from "react-router-dom";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
/** 
 * Determine Chip color based on user status
 */
const getStatusStyle = (status) => {
  switch (status) {
    case "active":
      return { color: "green", text: "Active" };
    case "pending":
      return { color: "orange", text: "Pending" };
    case "trial":
      return { color: "orange", text: "Trial" };
    case "suspended":
      return { color: "red", text: "Suspended" };
    case "blocked":
      return { color: "red", text: "Blocked" };
    default:
      return { color: "gray", text: "Unknown" };
  }
};

const getSubscriptionBorderColor = (status) => {
  switch (status) {
    case "incomplete":
    case "incomplete_expired":
      return "#FFA726"; // orange-ish
    case "trialing":
      return "#26C6DA"; // cyan
    case "active":
      return "#66BB6A"; // green
    case "past_due":
      return "#FB8C00"; // darker orange
    case "canceled":
    case "unpaid":
    case "paused":
      return "#EF5350"; // red
    default:
      return "#1976d2"; // fallback
  }
};

const navLinks = [];

const ProfilePage = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  // Basic user info and role-based profile
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Local states for inline editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);

  const [displayName, setDisplayName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  // Read-only info (e.g. number_of_students / number_of_teachers)
  const [readOnlyInfo, setReadOnlyInfo] = useState({});
  const [isMissingProfile, setIsMissingProfile] = useState(false);

  // Fetch profile data
  const getProfileData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get("/profile");
      const { user: userData, profile: profileData, subscription: subscriptionData } =
        response.data.data;

      setUser(userData);
      setProfile(profileData || {});
      setSubscription(subscriptionData || null);
      if (!profileData) {
        setIsMissingProfile(true);
      }

      // Determine which field to show as "displayName"
      let roleBasedName = "";
      if (userData.role === "family") {
        roleBasedName = profileData.family_name || "";
      } else if (userData.role === "school") {
        roleBasedName = profileData.school_name || "";
      } else {
        // teacher or student
        roleBasedName = profileData.full_name || "";
      }
      setDisplayName(roleBasedName);

      // Phone number from user object
      setPhoneNumber(userData.phone_number || "");

      // Prepare read-only info
      const newReadOnlyInfo = {};
      if ("number_of_students" in profileData) {
        newReadOnlyInfo["Number of Students"] = profileData.number_of_students;
      }
      if ("number_of_teachers" in profileData) {
        newReadOnlyInfo["Number of Teachers"] = profileData.number_of_teachers;
      }
      setReadOnlyInfo(newReadOnlyInfo);

      setIsLoading(false);
    } catch (err) {
      if (user?.status === 'trial'){
        toast.error('you need to purchase a paid plan first')
      }
      else {
        toast.error("Error fetching profile data:", err);
      }
      setIsLoading(false);
    }
  };

  // Inline editing: "displayName"
  const handleSaveDisplayName = async () => {
    try {
      let payload = {};
      if (user.role === "family") {
        payload.family_name = displayName;
      } else if (user.role === "school") {
        payload.school_name = displayName;
      } else {
        // student or teacher
        payload.full_name = displayName;
      }

      await axios.post("/profile/update", payload);
      await getProfileData();
      setIsEditingName(false);
    } catch (error) {
      if (user?.status === 'trial'){
        toast.error('you need to purchase a paid plan first')
      }else {
        toast.error("Error updating display name:", error);
      }
    }
  };

  // Inline editing: "phoneNumber"
  const handleSavePhoneNumber = async () => {
    try {
      const payload = { phone_number: phoneNumber };
      await axios.post("/profile/update", payload);
      await getProfileData();
      setIsEditingPhone(false);
    } catch (error) {
      if (user?.status === 'trial'){
        toast.error('you need to purchase a paid plan first')
      }else {
        toast.error("Error updating phone number:", error);
      }
    }
  };

  const toggleAutoRenewal = async (subscriptionId, autoRenewValue) => {
    try {
      const response = await axios.post("/subscription/toggle-auto-renew", {
        subscription_id: subscriptionId,
        auto_renew: autoRenewValue,
      });
      return response.data;
    } catch (error) {
      if (user?.status === 'trial'){
        toast.error('you need to purchase a paid plan first')
      }else {
        toast.error("Error toggling auto-renewal:", error);
      }
      throw error;
    }
  };

  const handleToggleAutoRenew = async (autoRenewValue) => {
    if (!subscription) return;
    try {
      setIsLoading(true);
      const result = await toggleAutoRenewal(subscription.id, autoRenewValue);
      setSubscription((prev) => ({
        ...prev,
        auto_renew: result.auto_renew,
      }));
      console.log("Toggled auto-renew:", result);
      setIsLoading(false);
    } catch (error) {
      if (user?.status === 'trial'){
        toast.error('you need to purchase a paid plan first')
      }else {
        toast.error("Failed to toggle auto-renew:", error);
      }
    }
  };

  useEffect(() => {
    getProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <Box className="profile-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />

      <Container maxWidth="md" className="profile-page-container page-content">
        <Typography variant="h4" className="profile-page-title" gutterBottom>
          My {user.role} Profile
        </Typography>

        {/* Profile Card */}
        <Card className="profile-card" sx={{ position: "relative" }}>
          <CardContent className="profile-card-content">
            {/* Status Ribbon */}
            <Box
              className="status-ribbon"
              sx={{
                position: "absolute",
                top: "1rem",
                right: "-2rem",
                transform: "rotate(45deg)",
                width: "120px",
                textAlign: "center",
                backgroundColor: getStatusStyle(user.status).color,
                py: 0.5,
                boxShadow: 1,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#fff",
                  textTransform: "uppercase",
                  fontWeight: "bold",
                }}
              >
                {user.status}
              </Typography>
            </Box>

            {/* Top row: Avatar + Username/Email */}
            <Grid container spacing={2} >
                <Grid item  xs={12}>
                    <Box 
                        display="flex" 
                        alignItems="center" 
                        gap={2} 
                        flexWrap="wrap" 
                    >
                    <Avatar
                        alt={user.username}
                        sx={{
                        width: 70,
                        height: 70,
                        color: "#fcfcfce7",
                        backgroundColor: "var(--primary-purple)",
                        }}
                    />
                    <Box>
                        <Typography variant="h6" className="profile-username">
                        {user.username}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                        {user.email}
                        </Typography>

                    </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Two-column layout for Display Name and Phone */}
            <Grid container spacing={2} mt={2}>
              {/* Display Name */}
              <Grid item xs={12} sm={6}>
                <Typography className="profile-label">
                  {user.role === "family" && "Family Name"}
                  {user.role === "school" && "School Name"}
                  {(user.role === "student" || user.role === "teacher") &&
                    "Full Name"}
                </Typography>

                <Box display="flex" alignItems="center" >
                  {isEditingName ? (
                    <>
                      <input
                        className="inline-edit-input"
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                      />
                      <IconButton onClick={handleSaveDisplayName}>
                        <CheckIcon color="primary" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Typography className="profile-value">
                        {displayName || "N/A"}
                      </Typography>
                      <IconButton onClick={() => setIsEditingName(true)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Grid>

              {/* Phone Number */}
              <Grid item xs={12} sm={6}>
                <Typography className="profile-label">Mobile Phone</Typography>
                <Box display="flex" alignItems="center">
                  <PhoneIcon
                    fontSize="small"
                    color="disabled"
                    style={{ marginRight: 6 }}
                  />
                  {isEditingPhone ? (
                    <>
                      <input
                        className="inline-edit-input"
                        type="text"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                      />
                      <IconButton onClick={handleSavePhoneNumber}>
                        <CheckIcon color="primary" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Typography
                        className="profile-value"
                        style={{ marginRight: 6 }}
                      >
                        {phoneNumber || "N/A"}
                      </Typography>
                      <IconButton onClick={() => setIsEditingPhone(true)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>

            {isMissingProfile && (
              <Button
              className="d-flex m-auto mt-5" 
              onClick={() => {navigate('/setup-profile')}}
              >
                  Complete your profile now
              </Button>
              )
            }

            {/* Read-only Info (Number of Students, Teachers, etc.) */}
            {Object.keys(readOnlyInfo).length > 0 && (
              <Grid container spacing={6} mt={2}>
                {Object.entries(readOnlyInfo).map(([label, value]) => (
                  <Grid item xs={12} sm={6} key={label}>
                    <Typography className="profile-label">{label}</Typography>
                    <Typography className="profile-value">
                      {value ?? "N/A"}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            )}
            {user.status === "trial" && (
              <Box mt={2} display="flex" justifyContent="center">
                <Button
                  onClick={() => navigate("/purchase-plan")}
                  className="primary-button"
                >
                  Purchase a Paid Plan
                </Button>
              </Box>
            )}
            {user.role === "family" &&(
              <>
                 <Typography className="family-support-message">
                    if you need any help, there is a dedicated support person available to help you
                </Typography>
                <Typography className="family-support-message">
                   <WhatsAppIcon /> +971 (0) 509910248
                </Typography>
              </>
            )}

          </CardContent>
        </Card>

        {/* Subscription Card */}
        {subscription && (
          <Card
            className="subscription-card"
            sx={{
              position: "relative",
              borderLeft: `4px solid ${getSubscriptionBorderColor(
                subscription.subscription_status
              )}`,
              overflow: "hidden",
              p: 0,
            }}
          >
            {/* HERO / BANNER SECTION */}
            <Box
              sx={{
                backgroundColor: subscription.auto_renew ? "#e1f5e2" : "#ffe2e2",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 2,
                py: 3,
              }}
            >
              {subscription.auto_renew ? (
                <>
                  <Typography
                    variant="h6"
                    sx={{ color: "#2e7d32", fontWeight: "bold" }}
                  >
                    Auto-Renewal: ON
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#555", mt: 1 }}>
                    We'll automatically renew your plan on{" "}
                    <strong>{subscription.current_period_end}</strong>.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="h6"
                    sx={{ color: "#c62828", fontWeight: "bold" }}
                  >
                    Auto-Renewal: OFF
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#555", mt: 1 }}>
                    Your plan will end on{" "}
                    <strong>{subscription.current_period_end}</strong>.
                  </Typography>
                </>
              )}
            </Box>

            {/* MAIN DETAILS */}
            <CardContent sx={{ pt: 2 }}>
              <Typography
                className="subscription-card-title"
                variant="h6"
                sx={{ mb: 2 }}
              >
                Subscription Details
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                {/* Left column: plan type, status */}
                <Box flex={1}>
                  <Typography variant="subtitle2" className="profile-label">
                    Plan Type
                  </Typography>
                  <Typography
                    variant="body1"
                    className="profile-value"
                    sx={{ mb: 2 }}
                  >
                    {subscription.plan_type}
                  </Typography>

                  <Typography variant="subtitle2" className="profile-label">
                    Subscription Status
                  </Typography>
                  <Typography
                    variant="body1"
                    className="profile-value"
                    sx={{ textTransform: "uppercase" }}
                  >
                    {subscription.subscription_status}
                  </Typography>
                </Box>

                {/* Right column: auto-renew toggle */}
                <Box
                  flex={1}
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                >
                  <Button
                    startIcon={ subscription?.auto_renew
                      ? <SyncDisabledIcon /> 
                      : <LoopIcon />
                    }
                    onClick={() =>
                      handleToggleAutoRenew(!subscription?.auto_renew)
                    }
                    className="cancel-subscription-button"
                    sx={{
                      textTransform: "none",
                      border: "1px solid",
                      borderColor: subscription.auto_renew
                        ? "success.main"
                        : "error.main",
                      color: subscription.auto_renew
                        ? "success.main"
                        : "error.main",
                      ":hover": {
                        backgroundColor: subscription.auto_renew
                          ? "success.main"
                          : "error.main",
                        color: "#fff",
                      },
                    }}
                  >
                    {subscription?.auto_renew
                      ? "Cancel Auto-Renewal"
                      : "Enable Auto-Renewal"}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
        <ToastContainer />
      </Container>
    </>
  );
};

export default ProfilePage;
